import React from 'react';
import { Link as GatsbyLink, withPrefix } from 'gatsby';
import * as styles from './LinkButton.module.css';
import Image from './Image';

/**
 * LinkButtonコンポーネントに渡すPropsの型定義です。
 * @property children ボタン内に表示されるテキスト
 * @property to 遷移先のパスまたはURL
 */
export interface LinkButtonProps {
  /**
   * ボタン内に表示されるテキストコンテンツです。
   */
  children: React.ReactNode;
  /**
   * リンク先のパスまたはURLです。
   * 内部リンク（例: `/about`）の場合はGatsbyの`<Link>`を、
   * 外部リンク（例: `https://...` or `mailto:...`）の場合は通常の`<a>`タグを使用します。
   */
  to: string;

  /**
   * ボタンを中央揃えにするかどうか
   */
  center?: boolean;

  /**
   * ボタンに追加するCSSクラス名です。
   */
  className?: string;

  /**
   * ボタンに追加するスタイルです。
   */
  style?: React.CSSProperties;

  /**
   * テキストと矢印のgap
   */
  gap?: string;
  /**
   * 幅1pxの黒いボーダーを付けるかどうか
   */
  bordered?: boolean;

  disabled?: boolean;
}

/**
 * プロジェクト共通のリンクボタンコンポーネント。
 * 黄色背景・中央テキスト＋右端矢印アイコン。
 * 内部リンク・外部リンクを自動判別。
 *
 * @param {LinkButtonProps} props - このコンポーネントが受け取るprops。
 * @returns {JSX.Element} レンダリングされたLinkButtonコンポーネント。
 */
const LinkButton: React.FC<LinkButtonProps> = ({
  children,
  to,
  center,
  className,
  style,
  gap,
  bordered,
  disabled,
}) => {
  const isExternal =
    to.startsWith('mailto:') ||
    /^(http|https)/.test(to) ||
    to.endsWith('.pdf') ||
    to.endsWith('.pptx');
  const mergeClassName = className
    ? `${styles.button} ${className}`
    : styles.button;
  const borderedClass = bordered ? styles.bordered : '';
  const mergedWithBorder = borderedClass
    ? `${mergeClassName} ${borderedClass}`
    : mergeClassName;
  const disabledClass = disabled ? styles.disabled : '';
  const buttonClassName = [
    mergedWithBorder,
    disabledClass,
    center ? styles.buttonCenter : '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <span className={styles.buttonContent} style={{ gap: gap }}>
      {children}
      <span className={styles.arrow} aria-hidden='true'>
        <Image src='/svg/arrow_right.svg' alt='' className={styles.arrowIcon} />
      </span>
    </span>
  );

  if (isExternal) {
    if (disabled) {
      return (
        <span
          className={buttonClassName}
          style={style}
          aria-disabled='true'
          tabIndex={-1}
        >
          {content}
        </span>
      );
    }

    return (
      <a
        href={to}
        className={buttonClassName}
        target='_blank'
        rel='noopener noreferrer'
        style={style}
      >
        {content}
        <span className={styles.visuallyHidden}>（新しいタブで開きます）</span>
      </a>
    );
  }

  if (disabled) {
    return (
      <span
        className={buttonClassName}
        style={style}
        aria-disabled='true'
        tabIndex={-1}
      >
        {content}
      </span>
    );
  }

  // 同一ページ内のハッシュリンクの場合は、window.location.hashを直接変更
  if (to.includes('#')) {
    const [path, hash] = to.split('#');
    const currentPath =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const isSamePage =
      !path || path === currentPath || withPrefix(path) === currentPath;

    if (isSamePage && hash) {
      return (
        <a
          href={to}
          className={buttonClassName}
          style={style}
          onClick={e => {
            e.preventDefault();
            window.location.hash = hash;
          }}
        >
          {content}
        </a>
      );
    }
  }

  return (
    <GatsbyLink to={to} className={buttonClassName} style={style}>
      {content}
    </GatsbyLink>
  );
};

export default LinkButton;
